/**
 * Decode audio Blob → PCM → MP3.
 * Usa solo public/voice/lame.min.js (Worker + fallback), sin import npm (MPEGMode).
 */
import { getMp3EncoderWorkerSource } from './mp3EncoderWorker'
import { loadLamejs, resolveLameScriptUrl } from './loadLamejs'
import { VOICE_MP3_BITRATE } from './types'

const WORKER_TIMEOUT_MS = 45000

/**
 * @param {Blob} blob
 * @returns {Promise<{ pcm: Float32Array, sampleRate: number }>}
 */
async function decodeToMonoPcm(blob) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext
  if (!AudioCtx) throw new Error('AudioContext no disponible')
  const ctx = new AudioCtx()
  try {
    const ab = await blob.arrayBuffer()
    const audioBuffer = await new Promise((resolve, reject) => {
      ctx.decodeAudioData(
        ab.slice(0),
        (buf) => resolve(buf),
        (err) => reject(err || new Error('decodeAudioData falló'))
      )
    })
    const sampleRate = audioBuffer.sampleRate
    const len = audioBuffer.length
    const mono = new Float32Array(len)
    const ch0 = audioBuffer.getChannelData(0)
    if (audioBuffer.numberOfChannels === 1) {
      mono.set(ch0)
    } else {
      const ch1 = audioBuffer.getChannelData(1)
      for (let i = 0; i < len; i++) {
        mono[i] = (ch0[i] + ch1[i]) * 0.5
      }
    }
    return { pcm: mono, sampleRate }
  } finally {
    try {
      await ctx.close()
    } catch (e) {
      /* ignore */
    }
  }
}

function floatTo16(input) {
  const out = new Int16Array(input.length)
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out
}

/**
 * @param {Float32Array} pcm
 * @param {number} sampleRate
 * @param {number} [bitrate]
 * @returns {Promise<Blob>}
 */
export async function encodeMp3Sync(pcm, sampleRate, bitrate = VOICE_MP3_BITRATE) {
  const lame = await loadLamejs()
  const samples = floatTo16(pcm)
  const mp3encoder = new lame.Mp3Encoder(1, sampleRate, bitrate)
  const blockSize = 1152
  const mp3Data = []
  for (let i = 0; i < samples.length; i += blockSize) {
    const slice = samples.subarray(i, i + blockSize)
    const mp3buf = mp3encoder.encodeBuffer(slice)
    if (mp3buf.length > 0) mp3Data.push(mp3buf)
  }
  const end = mp3encoder.flush()
  if (end.length > 0) mp3Data.push(end)
  return new Blob(mp3Data, { type: 'audio/mpeg' })
}

function encodeViaWorker(pcm, sampleRate, bitrate) {
  return new Promise((resolve, reject) => {
    let worker
    let url
    let timer
    const id = `mp3-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    try {
      const src = getMp3EncoderWorkerSource(resolveLameScriptUrl())
      const blob = new Blob([src], { type: 'application/javascript' })
      url = URL.createObjectURL(blob)
      worker = new Worker(url)
    } catch (e) {
      reject(e)
      return
    }

    const cleanup = () => {
      if (timer) clearTimeout(timer)
      try {
        worker.terminate()
      } catch (e) {
        /* ignore */
      }
      if (url) {
        try {
          URL.revokeObjectURL(url)
        } catch (e) {
          /* ignore */
        }
      }
    }

    timer = setTimeout(() => {
      cleanup()
      reject(new Error('Timeout encoding MP3'))
    }, WORKER_TIMEOUT_MS)

    worker.onmessage = (ev) => {
      const msg = ev.data || {}
      if (msg.id !== id) return
      cleanup()
      if (msg.type === 'ok' && msg.buffer) {
        resolve(new Blob([msg.buffer], { type: msg.mimeType || 'audio/mpeg' }))
      } else {
        reject(new Error(msg.message || 'Worker MP3 error'))
      }
    }
    worker.onerror = (err) => {
      cleanup()
      reject(err && err.message ? new Error(err.message) : new Error('Worker MP3 crash'))
    }

    const ab = pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength)
    worker.postMessage(
      {
        type: 'encode',
        id,
        pcm: ab,
        pcmType: 'float32',
        sampleRate,
        bitrate,
        channels: 1,
      },
      [ab]
    )
  })
}

/**
 * @param {Blob} inputBlob
 * @param {{ bitrate?: number }} [opts]
 * @returns {Promise<Blob>}
 */
export async function encodeMp3(inputBlob, opts = {}) {
  if (!inputBlob || !inputBlob.size) {
    throw new Error('Audio vacío')
  }
  const bitrate = opts.bitrate || VOICE_MP3_BITRATE
  const { pcm, sampleRate } = await decodeToMonoPcm(inputBlob)

  try {
    return await encodeViaWorker(pcm, sampleRate, bitrate)
  } catch (workerErr) {
    console.warn('[voice] Worker MP3 falló, usando hilo principal:', workerErr)
    return encodeMp3Sync(pcm, sampleRate, bitrate)
  }
}

export default encodeMp3
